const STORAGE_SCHEMA = 'animus.arena.chat-intents.v1'
const STORAGE_PREFIX = 'animus.arena.chat-intents.v1'
const REQUEST_SCHEMA = 'animus.arena.chat-send-request.v1'
const INTENT_SCHEMA = 'animus.arena.chat-send-intent.v1'

export const DEFAULT_CHAT_INTENT_TTL_MS = 24 * 60 * 60 * 1_000
export const DEFAULT_CHAT_INTENT_CAPACITY = 32
export const DEFAULT_CHAT_INTENT_MAX_BYTES = 32 * 1_024

export interface ChatIntentScope {
  userId: string
  workspaceId: string
  projectId: string
  /** Server-minted, non-secret binding for one authenticated Arena session. */
  sessionBinding: string
}

export interface ChatIntentBinding {
  conversationId: string
  agentId?: string
  revision: number
}

export interface PendingChatIntent extends ChatIntentBinding {
  intentHash: string
  requestHash: string
  controlsHash?: string
  idempotencyKey: string
  createdAt: number
  expiresAt: number
}

export interface ChatIntentStorage {
  readonly length?: number
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  key?(index: number): string | null
}

export interface ChatIntentJournalOptions {
  scope: ChatIntentScope
  storage?: ChatIntentStorage
  now?: () => number
  ttlMs?: number
  capacity?: number
  maxBytes?: number
  /** Browser mode removes stale partitions from prior authenticated sessions. */
  pruneForeignPartitions?: boolean
}

interface StoredEnvelope {
  schema: typeof STORAGE_SCHEMA
  intents: PendingChatIntent[]
}

class MemoryChatIntentStorage implements ChatIntentStorage {
  private readonly values = new Map<string, string>()

  getItem(key: string): string | null {
    return this.values.get(key) ?? null
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value)
  }

  removeItem(key: string): void {
    this.values.delete(key)
  }
}

function boundedIdentifier(value: unknown, maximum = 512): value is string {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= maximum &&
    value === value.trim() &&
    !/[\u0000-\u001f\u007f]/.test(value)
}

function hashString(value: unknown): value is string {
  return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)
}

function storedIntent(value: unknown): PendingChatIntent | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  const agentId = source.agentId
  if (
    !hashString(source.intentHash) ||
    !hashString(source.requestHash) ||
    !(source.controlsHash === undefined || hashString(source.controlsHash)) ||
    typeof source.idempotencyKey !== 'string' ||
    !/^[A-Za-z0-9._:-]{16,200}$/.test(source.idempotencyKey) ||
    !boundedIdentifier(source.conversationId, 256) ||
    !(agentId === undefined || boundedIdentifier(agentId)) ||
    !Number.isSafeInteger(source.revision) ||
    Number(source.revision) < 0 ||
    !Number.isSafeInteger(source.createdAt) ||
    !Number.isSafeInteger(source.expiresAt) ||
    Number(source.expiresAt) <= Number(source.createdAt)
  ) return null
  return {
    intentHash: source.intentHash,
    requestHash: source.requestHash,
    ...(typeof source.controlsHash === 'string' ? { controlsHash: source.controlsHash } : {}),
    idempotencyKey: source.idempotencyKey,
    conversationId: source.conversationId,
    ...(typeof agentId === 'string' ? { agentId } : {}),
    revision: Number(source.revision),
    createdAt: Number(source.createdAt),
    expiresAt: Number(source.expiresAt),
  }
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

function exactRequest(
  scope: ChatIntentScope,
  conversationId: string,
  message: string,
  agentId?: string,
  controlsHash?: string,
): string {
  // Revision is persisted as the original receipt-validation binding below,
  // but excluded from this request hash because the server may advance it while
  // reserving this exact operation. Agent identity is immutable request meaning.
  return JSON.stringify({
    schema: REQUEST_SCHEMA,
    method: 'POST',
    route: '/api/application/v1/chats/:id/messages',
    user_id: scope.userId,
    workspace_id: scope.workspaceId,
    project_id: scope.projectId,
    session_binding: scope.sessionBinding,
    chat_id: conversationId,
    message,
    agent_id: agentId ?? null,
    ...(controlsHash ? { application_controls_hash: controlsHash } : {}),
  })
}

function exactIntent(
  scope: ChatIntentScope,
  conversationId: string,
  message: string,
): string {
  return JSON.stringify({
    schema: INTENT_SCHEMA,
    user_id: scope.userId,
    workspace_id: scope.workspaceId,
    project_id: scope.projectId,
    session_binding: scope.sessionBinding,
    chat_id: conversationId,
    message,
  })
}

function validScope(scope: ChatIntentScope): boolean {
  return boundedIdentifier(scope.userId) &&
    boundedIdentifier(scope.workspaceId) &&
    boundedIdentifier(scope.projectId) &&
    boundedIdentifier(scope.sessionBinding)
}

/** Reload-safe, content-minimizing journal for uncertain chat mutations. */
export class ChatIntentJournal {
  private readonly storage: ChatIntentStorage
  private readonly now: () => number
  private readonly ttlMs: number
  private readonly capacity: number
  private readonly maxBytes: number
  private readonly scopeKey: Promise<string>

  constructor(private readonly options: ChatIntentJournalOptions) {
    if (!validScope(options.scope)) throw new Error('Invalid chat intent scope')
    this.storage = options.storage ?? new MemoryChatIntentStorage()
    this.now = options.now ?? Date.now
    this.ttlMs = options.ttlMs ?? DEFAULT_CHAT_INTENT_TTL_MS
    this.capacity = options.capacity ?? DEFAULT_CHAT_INTENT_CAPACITY
    this.maxBytes = options.maxBytes ?? DEFAULT_CHAT_INTENT_MAX_BYTES
    if (!Number.isSafeInteger(this.ttlMs) || this.ttlMs < 1 || this.ttlMs > 7 * 24 * 60 * 60 * 1_000) {
      throw new Error('Invalid chat intent TTL')
    }
    if (!Number.isSafeInteger(this.capacity) || this.capacity < 1 || this.capacity > 128) {
      throw new Error('Invalid chat intent capacity')
    }
    if (!Number.isSafeInteger(this.maxBytes) || this.maxBytes < 1_024 || this.maxBytes > 128 * 1_024) {
      throw new Error('Invalid chat intent storage bound')
    }
    this.scopeKey = sha256(JSON.stringify([
      STORAGE_SCHEMA,
      options.scope.userId,
      options.scope.workspaceId,
      options.scope.projectId,
      options.scope.sessionBinding,
    ])).then((hash) => `${STORAGE_PREFIX}:${hash}`)
      .then((key) => {
        if (options.pruneForeignPartitions) this.pruneForeignPartitions(key)
        return key
      })
  }

  async claim(
    binding: ChatIntentBinding,
    normalizedMessage: string,
    normalizedControls?: string,
  ): Promise<PendingChatIntent> {
    if (
      !boundedIdentifier(binding.conversationId, 256) ||
      !(binding.agentId === undefined || boundedIdentifier(binding.agentId)) ||
      !Number.isSafeInteger(binding.revision) ||
      binding.revision < 0 ||
      normalizedMessage.length === 0
    ) throw new Error('Invalid chat intent binding')

    const controlsHash = normalizedControls ? await sha256(normalizedControls) : undefined
    const intentHash = await sha256(exactIntent(
      this.options.scope,
      binding.conversationId,
      normalizedMessage,
    ))
    const intents = await this.load()
    const existing = intents.find((intent) => intent.intentHash === intentHash)
    if (existing) {
      if (existing.controlsHash !== controlsHash) {
        throw new Error('Pending chat intent controls do not match the uncertain request')
      }
      const expectedRequestHash = await sha256(exactRequest(
        this.options.scope,
        existing.conversationId,
        normalizedMessage,
        existing.agentId,
        controlsHash,
      ))
      if (existing.requestHash === expectedRequestHash) return existing
      await this.save(intents.filter((intent) => intent !== existing))
    }

    const current = existing ? await this.load() : intents
    if (current.length >= this.capacity) {
      throw new Error('Pending chat intent capacity exceeded')
    }
    const now = this.now()
    const created: PendingChatIntent = {
      intentHash,
      requestHash: await sha256(exactRequest(
        this.options.scope,
        binding.conversationId,
        normalizedMessage,
        binding.agentId,
        controlsHash,
      )),
      ...(controlsHash ? { controlsHash } : {}),
      idempotencyKey: crypto.randomUUID(),
      conversationId: binding.conversationId,
      ...(binding.agentId ? { agentId: binding.agentId } : {}),
      revision: binding.revision,
      createdAt: now,
      expiresAt: now + this.ttlMs,
    }
    await this.save([...current, created])
    return created
  }

  async clear(intent: PendingChatIntent): Promise<void> {
    const intents = await this.load()
    await this.save(intents.filter((candidate) => !(
      candidate.intentHash === intent.intentHash &&
      candidate.requestHash === intent.requestHash &&
      candidate.idempotencyKey === intent.idempotencyKey
    )))
  }

  private async load(): Promise<PendingChatIntent[]> {
    const key = await this.scopeKey
    let raw: string | null
    try {
      raw = this.storage.getItem(key)
    } catch {
      throw new Error('Chat intent persistence is unavailable')
    }
    if (!raw) return []
    if (new TextEncoder().encode(raw).byteLength > this.maxBytes) {
      this.remove(key)
      return []
    }
    let decoded: unknown
    try {
      decoded = JSON.parse(raw) as unknown
    } catch {
      this.remove(key)
      return []
    }
    if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
      this.remove(key)
      return []
    }
    const envelope = decoded as Partial<StoredEnvelope>
    if (envelope.schema !== STORAGE_SCHEMA || !Array.isArray(envelope.intents)) {
      this.remove(key)
      return []
    }
    const now = this.now()
    const valid = envelope.intents
      .slice(0, this.capacity + 1)
      .map(storedIntent)
      .filter((intent): intent is PendingChatIntent => intent !== null && intent.expiresAt > now)
    if (valid.length > this.capacity) {
      this.remove(key)
      return []
    }
    if (valid.length !== envelope.intents.length) await this.save(valid)
    return valid
  }

  private async save(intents: PendingChatIntent[]): Promise<void> {
    const key = await this.scopeKey
    if (intents.length === 0) {
      this.remove(key)
      return
    }
    const value = JSON.stringify({ schema: STORAGE_SCHEMA, intents } satisfies StoredEnvelope)
    if (new TextEncoder().encode(value).byteLength > this.maxBytes) {
      throw new Error('Pending chat intent storage bound exceeded')
    }
    try {
      this.storage.setItem(key, value)
    } catch {
      throw new Error('Chat intent persistence is unavailable')
    }
  }

  private remove(key: string): void {
    try {
      this.storage.removeItem(key)
    } catch {
      throw new Error('Chat intent persistence is unavailable')
    }
  }

  private pruneForeignPartitions(currentKey: string): void {
    if (typeof this.storage.length !== 'number' || typeof this.storage.key !== 'function') return
    try {
      const stale: string[] = []
      for (let index = 0; index < this.storage.length; index += 1) {
        const key = this.storage.key(index)
        if (key?.startsWith(`${STORAGE_PREFIX}:`) && key !== currentKey) stale.push(key)
      }
      for (const key of stale) this.storage.removeItem(key)
    } catch {
      throw new Error('Chat intent persistence is unavailable')
    }
  }
}
